import { index, jsonb, pgTable, text, timestamp, uniqueIndex, uuid } from "drizzle-orm/pg-core";
/** Lucien internal ontology for Mixpanel / product events (Prod Spec). */
export const LUCIEN_ANALYTICS_SEMANTIC_CATEGORIES = [
    "activation",
    "engagement",
    "collaboration",
    "monetisation",
    "failure",
    "abandonment",
    "exploration",
    "productivity",
    "unknown",
];
import { organisations } from "./organisations";
import { organisationIntegrations } from "./integrations";
/**
 * Lightweight denormalized catalogue of analytics events.
 * Supports "what events exist" for LLM + query translation.
 */
export const analyticsEventCatalogue = pgTable("analytics_event_catalogue", {
    id: uuid("id").primaryKey().defaultRandom(),
    companyId: uuid("company_id")
        .notNull()
        .references(() => organisations.id, { onDelete: "cascade" }),
    integrationId: uuid("integration_id")
        .notNull()
        .references(() => organisationIntegrations.id, { onDelete: "cascade" }),
    eventName: text("event_name").notNull(),
    semanticCategory: text("semantic_category"),
    productArea: text("product_area"),
    firstSeenAt: timestamp("first_seen_at", { withTimezone: true }),
    lastSeenAt: timestamp("last_seen_at", { withTimezone: true }),
    propertyNames: jsonb("property_names").$type().default([]),
    frequencyScore: text("frequency_score"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    companyIdx: index("idx_analytics_event_catalogue_company").on(table.companyId),
    integrationIdx: index("idx_analytics_event_catalogue_integration").on(table.integrationId),
}));
/**
 * Cursor for incremental analytics sync.
 */
export const analyticsIngestionCursor = pgTable("analytics_ingestion_cursor", {
    integrationId: uuid("integration_id")
        .primaryKey()
        .references(() => organisationIntegrations.id, { onDelete: "cascade" }),
    lastEventTimestamp: timestamp("last_event_timestamp", { withTimezone: true }),
    lastCohortSyncAt: timestamp("last_cohort_sync_at", { withTimezone: true }),
    lastFunnelSyncAt: timestamp("last_funnel_sync_at", { withTimezone: true }),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    updatedIdx: index("idx_analytics_ingestion_cursor_updated").on(table.updatedAt),
}));
/**
 * Durable Mixpanel-derived artefacts (funnel / retention / cohort summaries).
 * Append rows with new `as_of`; readers take latest by `(integration_id, kind, snapshot_key)`.
 */
export const analyticsMixpanelSnapshots = pgTable("analytics_mixpanel_snapshots", {
    id: uuid("id").primaryKey().defaultRandom(),
    companyId: uuid("company_id")
        .notNull()
        .references(() => organisations.id, { onDelete: "cascade" }),
    integrationId: uuid("integration_id")
        .notNull()
        .references(() => organisationIntegrations.id, { onDelete: "cascade" }),
    kind: text("kind").notNull().$type(),
    snapshotKey: text("snapshot_key").notNull(),
    asOf: timestamp("as_of", { withTimezone: true }).notNull(),
    payload: jsonb("payload").notNull().default({}).$type(),
    metadata: jsonb("metadata").notNull().default({}).$type(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, table => ({
    integrationKindKeyIdx: index("idx_analytics_mixpanel_snapshots_int_kind_key_asof").on(table.integrationId, table.kind, table.snapshotKey, table.asOf),
    companyIdx: index("idx_analytics_mixpanel_snapshots_company").on(table.companyId),
}));
/**
 * Inferred mapping from Lucien metric keys → Mixpanel event name candidates (learned layer).
 */
export const analyticsMetricHypothesis = pgTable("analytics_metric_hypothesis", {
    id: uuid("id").primaryKey().defaultRandom(),
    companyId: uuid("company_id")
        .notNull()
        .references(() => organisations.id, { onDelete: "cascade" }),
    integrationId: uuid("integration_id")
        .notNull()
        .references(() => organisationIntegrations.id, { onDelete: "cascade" }),
    metricKey: text("metric_key").notNull(),
    candidates: jsonb("candidates")
        .notNull()
        .default([])
        .$type(),
    lockedEvent: text("locked_event"),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    uniqueIntegrationMetric: uniqueIndex("uq_analytics_metric_hypothesis_int_metric").on(table.integrationId, table.metricKey),
}));
